import React from "react";
import { useEffectOnce } from "react-use";
import { useRestApiProvider } from "@jugl-web/rest-api";
import { AuthStep, LoginMethod } from "./types";
import LoginForm from "./components/LoginForm";
import OtpVerification from "./components/OtpVerification";
import { LoginPageProvider } from "./providers/LoginPageProvider";
import { useLoginPage } from "./providers/LoginPageProvider/useLoginPage";
import { SignUpFlowLayout } from "../SignUpFlowLayout";

const LoginPageContent: React.FC = () => {
  const { authStep, lastLoginMethod, isLoading, setLoginMethod, setAuthStep } =
    useLoginPage();
  const { authApi } = useRestApiProvider();
  const [getRequestCountry] = authApi.useLazyRequestCountryQuery();
  useEffectOnce(() => {
    if (lastLoginMethod) {
      setLoginMethod(lastLoginMethod);
      return;
    }
    const requestCountry = async () => {
      const response = await getRequestCountry();
      const requestedCountryCode = response.data;
      if (requestedCountryCode?.country === "IN") {
        setLoginMethod(LoginMethod.phone);
        return;
      }
      if (requestedCountryCode?.country === "US") {
        setLoginMethod(LoginMethod.email);
        return;
      }

      const setLoginMethodByCountry = (countryCode: string) => {
        // Define regions where email is preferred (USA and Europe)
        const emailPreferredCountries = [
          // North America
          "US",
          "CA",
          // Western Europe
          "GB",
          "DE",
          "FR",
          "IT",
          "ES",
          "PT",
          "NL",
          "BE",
          "LU",
          "CH",
          "AT",
          // Northern Europe
          "SE",
          "NO",
          "DK",
          "FI",
          "IS",
          "PL",
          "CZ",
          "SK",
          "HU",
          "RO",
          "BG",
          "HR",
          "SI",
          "EE",
          "LV",
          "LT",
        ];

        if (emailPreferredCountries.includes(countryCode)) {
          setLoginMethod(LoginMethod.email);
        } else {
          setLoginMethod(LoginMethod.phone);
        }
      };

      const { locale } = Intl.DateTimeFormat().resolvedOptions();
      const countryFromLocale = locale.split("-")[1];

      if (countryFromLocale) {
        setLoginMethodByCountry(countryFromLocale);
      } else {
        setLoginMethod(LoginMethod.email);
      }
    };
    requestCountry();
  });

  return (
    <SignUpFlowLayout
      type="login"
      showTermsAndConditions
      isLoading={isLoading}
      footerClassName="pb-6"
      onBackClick={
        authStep === AuthStep.otp
          ? () => {
              setAuthStep(AuthStep.login);
            }
          : undefined
      }
    >
      <div className="flex w-full justify-center pb-4 pt-[34px] 2xl:pt-[88.5px]">
        <div className="h-full max-w-[704px]">
          <div className="flex flex-col">
            {authStep === AuthStep.login ? <LoginForm /> : <OtpVerification />}
          </div>
        </div>
      </div>
    </SignUpFlowLayout>
  );
};

const LoginPage: React.FC = () => (
  <LoginPageProvider>
    <LoginPageContent />
  </LoginPageProvider>
);

export default LoginPage;
